import html2canvas from "html2canvas";

export const POSTER_WIDTH = 414;
export const POSTER_HEIGHT = Math.round((POSTER_WIDTH * 16) / 9);
const CAPTURE_SCALE = 2;

const MAIN_FONT_SIZES = [11, 12, 13, 15, 28];

function sanitizeFilename(name) {
    return String(name || "selection")
        .trim()
        .replace(/[^\w\u4e00-\u9fff\- ]+/g, "")
        .replace(/\s+/g, "-")
        .slice(0, 60) || "selection";
}

function getMainFontsUrl() {
    const base = process.env.PUBLIC_URL || "";
    const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
    return `${window.location.origin}${prefix}/fonts/1.ttf`;
}

export function formatPosterExpiry(expiresAtLabel) {
    const raw = String(expiresAtLabel || "").trim();
    if (!raw) {
        return "—";
    }
    const datePart = raw.split(" ")[0];
    return datePart.replace(/-/g, ".");
}

export async function ensurePosterFontsLoaded() {
    if (!document.fonts?.load) {
        return;
    }

    try {
        await Promise.race([
            Promise.all(MAIN_FONT_SIZES.map((size) => document.fonts.load(`${size}px MainFonts`))),
            document.fonts.ready,
            new Promise((resolve) => {
                window.setTimeout(resolve, 2500);
            }),
        ]);
    } catch {
        // Fallback stack will still render if custom font is unavailable.
    }
}

function prepareCloneForCapture(clonedDoc, clonedElement) {
    const style = clonedDoc.createElement("style");
    style.textContent = `
        @font-face {
            font-family: MainFonts;
            src: url("${getMainFontsUrl()}") format("truetype");
        }
        .selection-poster {
            width: ${POSTER_WIDTH}px !important;
            height: ${POSTER_HEIGHT}px !important;
            max-width: none !important;
            aspect-ratio: unset !important;
            margin: 0 !important;
            padding: 48px 0 40px !important;
            box-sizing: border-box !important;
        }
        .selection-poster__inner {
            padding: 0 32px !important;
            box-sizing: border-box !important;
        }
        .selection-poster__brand,
        .selection-poster__title,
        .selection-poster__subtitle,
        .selection-poster__expiry,
        .selection-poster__website {
            font-family: MainFonts, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        }
        .selection-poster__brand {
            padding-left: 0.45em;
        }
        .selection-poster__website {
            padding-left: 0.35em;
        }
    `;
    clonedDoc.head.appendChild(style);

    if (clonedElement) {
        clonedElement.style.width = `${POSTER_WIDTH}px`;
        clonedElement.style.height = `${POSTER_HEIGHT}px`;
        clonedElement.style.maxWidth = "none";
        clonedElement.style.aspectRatio = "unset";
        clonedElement.style.margin = "0";
    }
}

function applyCaptureDimensions(element) {
    element.style.width = `${POSTER_WIDTH}px`;
    element.style.height = `${POSTER_HEIGHT}px`;
    element.style.maxWidth = `${POSTER_WIDTH}px`;
    element.style.aspectRatio = "unset";
    element.style.margin = "0";
    element.style.boxSizing = "border-box";
}

function restoreCaptureDimensions(element, previous) {
    element.style.width = previous.width;
    element.style.height = previous.height;
    element.style.maxWidth = previous.maxWidth;
    element.style.aspectRatio = previous.aspectRatio;
    element.style.margin = previous.margin;
    element.style.boxSizing = previous.boxSizing;
}

function normalizeCanvasSize(canvas) {
    const targetW = POSTER_WIDTH * CAPTURE_SCALE;
    const targetH = POSTER_HEIGHT * CAPTURE_SCALE;

    if (canvas.width === targetW && canvas.height === targetH) {
        return canvas;
    }

    const normalized = document.createElement("canvas");
    normalized.width = targetW;
    normalized.height = targetH;
    const ctx = normalized.getContext("2d");
    if (!ctx) {
        return canvas;
    }

    ctx.fillStyle = "#fbfbfd";
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(
        canvas,
        0,
        0,
        Math.min(canvas.width, targetW),
        Math.min(canvas.height, targetH),
        0,
        0,
        Math.min(canvas.width, targetW),
        Math.min(canvas.height, targetH)
    );

    return normalized;
}

export async function captureSelectionPoster(element) {
    if (!element) {
        throw new Error("Poster element is missing");
    }

    await ensurePosterFontsLoaded();

    const previous = {
        width: element.style.width,
        height: element.style.height,
        maxWidth: element.style.maxWidth,
        aspectRatio: element.style.aspectRatio,
        margin: element.style.margin,
        boxSizing: element.style.boxSizing,
    };

    applyCaptureDimensions(element);

    try {
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const canvas = await html2canvas(element, {
            scale: CAPTURE_SCALE,
            backgroundColor: "#fbfbfd",
            useCORS: true,
            logging: false,
            scrollX: 0,
            scrollY: 0,
            onclone: prepareCloneForCapture,
        });

        return normalizeCanvasSize(canvas);
    } finally {
        restoreCaptureDimensions(element, previous);
    }
}

export async function downloadSelectionPosterFromElement(element, albumName) {
    const canvas = await captureSelectionPoster(element);
    const link = document.createElement("a");
    link.download = `${sanitizeFilename(albumName)}-selection-poster.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
}
